/**
 * Copyright schukai GmbH and contributors 2023. All Rights Reserved.
 * Node module: @schukai/monster
 * This file is licensed under the AGPLv3 License.
 * License text available at https://www.gnu.org/licenses/agpl-3.0.en.html
 */

import { Base } from "./base.mjs";
import { isObject } from "./is.mjs";
import { TokenList } from "./tokenlist.mjs";
import { UniqueQueue } from "./uniquequeue.mjs";
import { instanceSymbol } from "../constants.mjs";
export { Observer };

/**
 * An observer manages a callback function
 *
 * The update method is called with the subject object as this pointer. For this reason
 * the callback should not be an arrow function, because it gets this pointer of its own context.
 *
 * Include this class in your project with the following code:
 *
 * ```js
 * import { Observer } from "@schukai/monster/source/types/observer.mjs";
 * ```
 *
 * The callback function is passed as the first argument to the constructor.
 *
 * ```js
 * new Observer(()=>{
 *     // this is not subject
 * })
 *
 * new Observer(function() {
 *     // this is subject
 * })
 * ```
 *
 * Additional arguments can be passed to the callback. To do this, simply specify them.
 *
 * ```js
 * Observer(function(a, b, c) {
 *     console.log(a, b, c); // ↦ "a", 2, true
 * }, "a", 2, true)
 * ```
 *
 * The callback function must have as many parameters as arguments are given.
 *
 * @externalExample ../../example/types/observer.mjs
 * @license AGPLv3
 * @since 1.0.0
 * @copyright schukai GmbH
 * @memberOf Monster.Types
 */
class Observer extends Base {
	/**
	 *
	 * @param {function} callback
	 * @param {*} args
	 */
	constructor(callback, ...args) {
		super();

		if (typeof callback !== "function") {
			throw new Error("observer callback must be a function");
		}

		this.callback = callback;
		this.arguments = args;
		this.tags = new TokenList();
		this.queue = new UniqueQueue();
	}

	/**
	 * This method is called by the `instanceof` operator.
	 * @returns {symbol}
	 * @since 2.1.0
	 */
	static get [instanceSymbol]() {
		return Symbol.for("@schukai/monster/types/observer");
	}

	/**
	 *
	 * @param {string} tag
	 * @returns {Observer}
	 */
	addTag(tag) {
		this.tags.add(tag);
		return this;
	}

	/**
	 *
	 * @param {string} tag
	 * @returns {Observer}
	 */
	removeTag(tag) {
		this.tags.remove(tag);
		return this;
	}

	/**
	 *
	 * @returns {Array}
	 */
	getTags() {
		return this.tags.entries();
	}

	/**
	 *
	 * @param {string} tag
	 * @returns {boolean}
	 */
	hasTag(tag) {
		return this.tags.contains(tag);
	}

	/**
	 *
	 * @param {object} subject
	 * @returns {Promise}
	 */
	update(subject) {
		const self = this;

		return new Promise(function (resolve, reject) {
			if (!isObject(subject)) {
				reject("subject must be an object");
				return;
			}

			self.queue.add(subject);

			setTimeout(() => {
				try {
					// the queue and the `setTimeout` ensure that an object is not
					// informed of the same change more than once.
					if (self.queue.isEmpty()) {
						resolve();
						return;
					}

					const s = self.queue.poll();
					const result = self.callback.apply(s, self.arguments);

					if (isObject(result) && result instanceof Promise) {
						result.then(resolve).catch(reject);
						return;
					}

					resolve(result);
				} catch (e) {
					reject(e);
				}
			}, 0);
		});
	}
}
